import type { EPCR } from '@/types';
import { cn } from '@/lib/cn';
import { ArrowDown, Shield, Flame, Crosshair } from 'lucide-react';
import { RuleTag } from '@/components/RuleTag';

/**
 * OtherTraumaSubSets — Foundation §4 trauma sub-sets beyond MVC +
 * Pediatric:
 *
 *   - Fall Sub-Set              (height, surface, mechanism)
 *   - Assault Sub-Set           (weapon type, LEO on scene)
 *   - Burn Sub-Set              (cause, depth, %TBSA, airway)
 *   - Penetrating Sub-Set       (mechanism, LEO on scene)
 *
 * Activation gating is handled by the conditional cascade engine.
 * TraumaTab decides which of these to render based on
 * isSetActive(epcr, 'trauma_fall' | 'trauma_assault' | 'trauma_burn' |
 * 'trauma_penetrating').
 *
 * These sub-sets are compact on purpose — their fields feed into the
 * same NEMSIS elements as the parent Trauma Set, so the UI is only
 * here to capture the mechanism-specific attributes the spec calls
 * out (fall height drives yellow criterion, burn TBSA drives ABA
 * triage, etc.).
 */

// =================================================================
// Fall Sub-Set (§4.5 yellow criterion "Fall > 10 ft")
// =================================================================

interface TraumaProps {
  epcr: EPCR;
  onUpdate: (patch: Partial<EPCR>) => void;
}

export function FallSubSet({ epcr, onUpdate }: TraumaProps) {
  const locked = epcr.locked;
  const height = epcr.trauma_fall_height_ft ?? null;
  const tripsYellow = height !== null && height > 10;

  return (
    <section className="border border-sky-800/60 bg-sky-950/10">
      <header className="flex items-center justify-between border-b border-sky-800/40 bg-sky-950/30 px-3 py-1.5">
        <div className="flex items-center gap-2">
          <ArrowDown size={14} className="text-sky-300" />
          <h3 className="text-[11px] font-bold uppercase tracking-widest text-sky-200">
            Fall Sub-Set
          </h3>
        </div>
        <RuleTag
          sources={[
            { standard: 'NEMSIS', rationale: 'Fall mechanism attributes for trauma registry.' },
            { standard: 'ACS-2021', rationale: 'Height > 10 ft triggers eInjury.04 YELLOW criterion.' },
          ]}
        />
      </header>
      <div className="p-3 space-y-3">
        <label className="flex flex-col gap-1 max-w-xs">
          <span className="text-[10px] uppercase tracking-widest text-slate-500">
            Fall Height (ft)
          </span>
          <input
            type="number"
            inputMode="decimal"
            step="0.5"
            value={height ?? ''}
            onChange={(e) => {
              const n = Number(e.target.value);
              onUpdate({ trauma_fall_height_ft: e.target.value === '' ? null : Number.isFinite(n) ? n : null });
            }}
            disabled={locked}
            className="input-field font-mono text-sm"
          />
        </label>
        <div
          className={cn(
            'border px-2.5 py-1.5 text-xs leading-snug',
            tripsYellow
              ? 'border-amber-700 bg-amber-950/40 text-amber-100'
              : 'border-slate-700 bg-slate-900/60 text-slate-400',
          )}
        >
          <span className="font-bold uppercase tracking-widest text-[10px] mr-1">
            ACS 2021 Yellow
          </span>
          {tripsYellow
            ? `Fall > 10 ft — "Fall from height > 10 feet" yellow criterion auto-applies (eInjury.04).`
            : 'Enter fall height. > 10 ft auto-flags the ACS yellow criterion.'}
        </div>
      </div>
    </section>
  );
}

// =================================================================
// Assault Sub-Set (§4.1)
// =================================================================

const ASSAULT_WEAPONS = [
  'Firearm',
  'Knife / Sharp object',
  'Blunt object',
  'Bodily force (punch, kick)',
  'Strangulation',
  'Other',
  'Unknown',
];

export function AssaultSubSet({ epcr, onUpdate }: TraumaProps) {
  const locked = epcr.locked;
  return (
    <section className="border border-rose-800/60 bg-rose-950/10">
      <header className="flex items-center justify-between border-b border-rose-800/40 bg-rose-950/30 px-3 py-1.5">
        <div className="flex items-center gap-2">
          <Shield size={14} className="text-rose-300" />
          <h3 className="text-[11px] font-bold uppercase tracking-widest text-rose-200">
            Assault Sub-Set
          </h3>
        </div>
        <RuleTag
          sources={[
            { standard: 'NEMSIS', rationale: 'Assault mechanism attributes for trauma registry.' },
            { standard: 'Department', rationale: 'Weapon type informs scene documentation + LEO coordination.' },
          ]}
        />
      </header>
      <div className="p-3 space-y-3">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
          Weapon Type
        </div>
        <div className="flex flex-wrap gap-1.5">
          {ASSAULT_WEAPONS.map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => onUpdate({ trauma_assault_weapon_type: w })}
              disabled={locked}
              className={cn(
                'px-2.5 py-1 text-xs border transition-colors',
                epcr.trauma_assault_weapon_type === w
                  ? 'bg-rose-600 text-white border-rose-400'
                  : 'bg-slate-800 text-slate-300 border-slate-600 hover:bg-slate-700',
              )}
            >
              {w}
            </button>
          ))}
        </div>
        <div className="border border-slate-700 bg-slate-900/60 px-2.5 py-1.5 text-[11px] leading-snug text-slate-400">
          <span className="font-bold uppercase tracking-widest text-[10px] mr-1">LEO coordination</span>
          Firearm, knife, or strangulation selections will surface law-enforcement
          coordination prompts at handoff. Document police presence + badge number
          in the scene narrative.
        </div>
      </div>
    </section>
  );
}

// =================================================================
// Burn Sub-Set (§4.1)
// =================================================================

const BURN_CAUSES = ['Thermal', 'Chemical', 'Electrical', 'Inhalation'];
const BURN_DEPTHS = ['Superficial (1°)', 'Partial Thickness (2°)', 'Full Thickness (3°)', 'Mixed'];

export function BurnSubSet({ epcr, onUpdate }: TraumaProps) {
  const locked = epcr.locked;
  const tbsa = epcr.trauma_burn_tbsa_pct ?? null;
  const abaFlagged = tbsa !== null && tbsa >= 10;

  return (
    <section className="border border-orange-800/60 bg-orange-950/10">
      <header className="flex items-center justify-between border-b border-orange-800/40 bg-orange-950/30 px-3 py-1.5">
        <div className="flex items-center gap-2">
          <Flame size={14} className="text-orange-300" />
          <h3 className="text-[11px] font-bold uppercase tracking-widest text-orange-200">
            Burn Sub-Set
          </h3>
        </div>
        <RuleTag
          sources={[
            { standard: 'NEMSIS', rationale: 'Burn cause + depth + %TBSA required for burn-center triage.' },
            { standard: 'Clinical', rationale: 'ABA burn-center referral criteria (≥ 10% TBSA partial/full thickness).' },
          ]}
        />
      </header>
      <div className="p-3 space-y-3">
        <div className="grid gap-3 sm:grid-cols-3">
          <SelectField
            label="Cause"
            options={BURN_CAUSES}
            value={epcr.trauma_burn_cause ?? ''}
            disabled={locked}
            onChange={(v) => onUpdate({ trauma_burn_cause: v || null })}
          />
          <SelectField
            label="Depth"
            options={BURN_DEPTHS}
            value={epcr.trauma_burn_depth ?? ''}
            disabled={locked}
            onChange={(v) => onUpdate({ trauma_burn_depth: v || null })}
          />
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-slate-500">
              % TBSA (Rule of Nines)
            </span>
            <input
              type="number"
              inputMode="decimal"
              step="1"
              min={0}
              max={100}
              value={tbsa ?? ''}
              onChange={(e) => {
                const n = Number(e.target.value);
                onUpdate({ trauma_burn_tbsa_pct: e.target.value === '' ? null : Number.isFinite(n) ? n : null });
              }}
              disabled={locked}
              className="input-field font-mono text-sm"
            />
          </label>
        </div>
        <div
          className={cn(
            'border px-2.5 py-1.5 text-xs leading-snug',
            abaFlagged || epcr.trauma_burn_cause === 'Inhalation'
              ? 'border-orange-600 bg-orange-950/40 text-orange-100'
              : 'border-slate-700 bg-slate-900/60 text-slate-400',
          )}
        >
          <span className="font-bold uppercase tracking-widest text-[10px] mr-1">ABA referral</span>
          {abaFlagged || epcr.trauma_burn_cause === 'Inhalation'
            ? `Meets ABA burn-center referral criteria. Recommend Rhode Island Hospital (Level I with burn-center capability) on Disposition.`
            : 'Partial/full thickness burns ≥ 10% TBSA or any inhalation injury warrant ABA burn-center referral.'}
        </div>
      </div>
    </section>
  );
}

// =================================================================
// Penetrating Sub-Set
// =================================================================

const PENETRATING_MECHANISMS = [
  'GSW — Single wound',
  'GSW — Multiple wounds',
  'Stabbing',
  'Impalement',
  'Shrapnel / Fragment',
  'Other',
];

export function PenetratingSubSet({ epcr, onUpdate }: TraumaProps) {
  const locked = epcr.locked;
  return (
    <section className="border border-red-800/60 bg-red-950/10">
      <header className="flex items-center justify-between border-b border-red-800/40 bg-red-950/30 px-3 py-1.5">
        <div className="flex items-center gap-2">
          <Crosshair size={14} className="text-red-300" />
          <h3 className="text-[11px] font-bold uppercase tracking-widest text-red-200">
            Penetrating Sub-Set
          </h3>
        </div>
        <RuleTag
          sources={[
            { standard: 'NEMSIS', rationale: 'Penetrating mechanism attributes for trauma registry.' },
            { standard: 'ACS-2021', rationale: 'Penetrating injury to head/neck/torso is a RED trauma criterion.' },
          ]}
        />
      </header>
      <div className="p-3 space-y-3">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
          Mechanism
        </div>
        <div className="flex flex-wrap gap-1.5">
          {PENETRATING_MECHANISMS.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => onUpdate({ trauma_penetrating_mechanism: m })}
              disabled={locked}
              className={cn(
                'px-2.5 py-1 text-xs border transition-colors',
                epcr.trauma_penetrating_mechanism === m
                  ? 'bg-red-600 text-white border-red-400'
                  : 'bg-slate-800 text-slate-300 border-slate-600 hover:bg-slate-700',
              )}
            >
              {m}
            </button>
          ))}
        </div>
        <div className="border border-red-800/40 bg-red-950/40 px-2.5 py-1.5 text-xs leading-snug text-red-200">
          <span className="font-bold uppercase tracking-widest text-[10px] mr-1">Trauma Alert</span>
          Penetrating injury to head, neck, torso, or proximal extremities is a RED
          criterion. Confirm that red triage criterion is selected on the Trauma tab
          so the destination prompt surfaces correctly.
        </div>
      </div>
    </section>
  );
}

// ------------------------------------------------------------------
function SelectField({
  label, options, value, disabled, onChange,
}: {
  label: string; options: string[]; value: string; disabled: boolean; onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="input-field text-sm"
      >
        <option value="">Select…</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
